import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "../../components/ui/Toaster";
import { LogOut, RadioTower } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { AuthService } from "../../services/authservice";
import { getOperationTimezone } from "../../utils/timezone";
import BeartrackLogo from "../../assets/beartrack/beartrack_logo.png";

// ─────────────────────────────────────────────────────────────────────────────
// Reloj en vivo en la zona horaria del operador logístico. Es puro adorno de la
// barra: la "última actualización" de los datos la maneja la propia pantalla.
// ─────────────────────────────────────────────────────────────────────────────
function LiveClock({ timeZone }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = now.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone,
  });
  const day = now.toLocaleDateString("es-CL", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone,
  });

  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="text-lg font-semibold tabular-nums text-fg">{time}</span>
      <span className="text-[11px] uppercase tracking-wide text-fg-muted">{day}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout kiosko full-screen para el "Centro de Control Logístico IA".
// Sin sidebar, sin menú, sin notificaciones: una sola pantalla de solo lectura
// pensada para un monitor de la sala de operaciones. La única acción es salir.
// ─────────────────────────────────────────────────────────────────────────────
export default function ControlTowerLayout({ routes }) {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeZone = useMemo(() => getOperationTimezone(user), [user]);

  useEffect(() => {
    if (!user && !isLoading) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  const handleLogout = () => {
    AuthService.logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-bg-subtle">
      <Toaster />

      {/* Barra superior mínima */}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-bg px-4 py-2.5 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <img src={BeartrackLogo} alt="BearTrack" className="h-7 w-auto shrink-0" />
          <span className="hidden h-6 w-px bg-border sm:block" />
          <span className="flex items-center gap-2 min-w-0 text-fg">
            <RadioTower className="h-5 w-5 text-brand-500 shrink-0" aria-hidden />
            <span className="truncate text-sm font-semibold md:text-base">
              {t("Centro de Control Logístico IA")}
            </span>
          </span>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <LiveClock timeZone={timeZone} />
          <button
            type="button"
            onClick={handleLogout}
            title={t("Salir")}
            aria-label={t("Salir")}
            className="grid h-9 w-9 place-items-center rounded-lg border border-border text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Contenido — la pantalla scrollea internamente si hace falta */}
      <main className="min-h-0 flex-1 overflow-auto">
        <Routes>
          {routes.map((route, index) => (
            <Route key={index} path={route.path} element={<route.element />} />
          ))}
        </Routes>
      </main>
    </div>
  );
}
